import { Injectable } from '@angular/core';
import { Observable, map, tap } from 'rxjs';

import { RestService } from '../../services/rest/rest.service';
import { UserProfileUtilsService } from '../../services/user-profile/user-profile-utils.service';
import { AddressUsageCd } from '../../domain/user-profile.domain';
import { PreferencesService } from '../../services/preferences/preferences.service';
import { PostalCodeService } from '../../features/postal-code/postal-code.service';

import {
  W8BenAddress,
  W8BenAddressUsageCd,
  W8BenContactAddress,
  W8BenFormBase64,
  W8BenFormProfile,
  W8BenIdentification,
  W8BenProfile,
  W8BenIdentificationType,
  W8BenFormDraft,
  W8BenFormDraftIdentification
} from './w8ben-form.domain';

@Injectable()
export class W8BenFormService {
  static readonly COUNTRY_CANADA: string = 'Canada';
  static readonly TIN_REGEX: RegExp = new RegExp('^[0-9]*$');
  profile: W8BenProfile;
  updatedProfile: W8BenFormProfile;
  updates: W8BenFormDraft = {};
  formBase64: W8BenFormBase64;
  haveMailingAddress: boolean = false;
  taxResidency: string = W8BenFormService.COUNTRY_CANADA;

  constructor(private restService: RestService,
              private userProfileUtilsService: UserProfileUtilsService,
              private preferencesService: PreferencesService
  ) { }

  getProfile(): Observable<W8BenProfile> {
    this.haveMailingAddress = false;
    this.updates = {};
    return this.restService.getResource('selfCertificationForm.retrieveProfile')
      .get<W8BenProfile>()
      .pipe(
        map((profile: W8BenProfile) => {
          this.taxResidency = this.getTaxResidencyFromProfile(profile);
          return {
            ...profile,
            legalName: this.getLegalName(profile),
            residenceAddressLines: this.getAddressLines(profile, 'HOME_ADDRESS'),
            mailingAddressLines: this.getAddressLines(profile, 'MAILING_ADDRESS')
          };
        }),
        tap((profile: W8BenProfile) => {
          this.profile = profile;
        })
      );
  }

  updateProfile(hasUpdates: boolean): Observable<W8BenFormBase64> | undefined {
    if(this.profile && this.updates) {
      this.setIdentification(this.getIdentification());

      if (!this.updates.streetAddresses?.length) {
        this.updates.streetAddresses = this.profile.contactStreetAddresses.map((contactAddress: W8BenContactAddress) => {
          return {
            usageCd: contactAddress.usageCd === 'HOME_ADDRESS' ? AddressUsageCd.PRIMARY_RESIDENCE : AddressUsageCd.CUSTOMER_MAILING,
            address: { ...contactAddress.address }
          };
        });
      }

      this.updatedProfile = {
        givenName: this.profile.givenName,
        middleName: this.profile.middleName,
        familyName: this.profile.familyName,
        citizenship: this.profile.citizenship,
        partyGroupResponseList: this.profile.partyGroupResponseList,
        birthDt: this.profile.birthDt,
        drafts: this.updates,
        updateType: [],
        ftinNotRequired: this.profile.ftinNotRequired
      }

      if (hasUpdates) {
        this.updatedProfile.updateType.push('ADDRESS');
      }
      // TODO: update after backend fixes for check address first in array
      this.updatedProfile.updateType.push('IDENTIFICATION');

      this.updatedProfile.drafts.identifications = this.updates.identifications?.filter(
        (identification: W8BenFormDraftIdentification) => identification.identification?.identificationNum !== ''
      );

      return this.restService
        .getResource('selfCertificationForm.updateProfile')
        .post<W8BenFormProfile, W8BenFormBase64>({
          ...this.updatedProfile,
          signed: false
        })
        .pipe(
          tap((data: W8BenFormBase64) => { this.formBase64 = data })
        );
    }
  }

  confirmUpdatedProfile(): Observable<W8BenFormBase64> | undefined {
    if(this.updatedProfile) {
      return this.restService
        .getResource('selfCertificationForm.updateProfile')
        .post<W8BenFormProfile, W8BenFormBase64>({
          ...this.updatedProfile,
          signed: true
        }).pipe(
          tap((data: W8BenFormBase64) => {
            this.formBase64 = data;
            this.updatePreference();
          })
        );
    }
  }

  getIdentification(type: W8BenIdentificationType = 'SIN'): string {

    if (!this.isTaxResidencyCanada() && type === 'SIN') {
      type = 'FTIN';
    }

    if (this.profile.ftinNotRequired && type === 'FTIN') {
      // FTIN is not required for non-Canadian tax residency for specific cases
      return '';
    }

    let identification = this.profile?.identifications?.find(
      (identification: W8BenIdentification) =>
        identification.typeCd === type
    );

    if (identification?.identificationNum === undefined || identification.identificationNum === '') {
      identification = this.updates.identifications?.find(
        (identification: W8BenFormDraftIdentification) =>
          identification.identification?.typeCd === type
      )?.identification;
    }

    return identification?.identificationNum ?? '';
  }

  setIdentification(identificationNum: string, type: W8BenIdentificationType = 'FTIN'): void {
    if (this.updates) {
      if (!this.updates.identifications) {
        this.updates.identifications = [];
      }

      const identification = this.updates.identifications.find(
        (identification: W8BenFormDraftIdentification) =>
          identification.identification?.typeCd === type
      );

      if (identification?.identification) {
        identification.identification.identificationNum = identificationNum;
      } else {
        this.updates.identifications.push({ identification: { identificationNum, typeCd: type } });
      }
    }
  }

  getTaxResidency(): string {
    return this.taxResidency;
  }

  isTaxResidencyCanada(): boolean {
    return this.getTaxResidency().toLowerCase() === W8BenFormService.COUNTRY_CANADA.toLowerCase();
  }

  private getLegalName(profile: W8BenProfile): string {
    const names = [
      profile.givenName ?? '',
      profile.middleName ?? '',
      profile.familyName ?? ''
    ];

    return names.filter((name: string) => name.trim() !== '').join(' ');
  }

  private formatAddress(address: W8BenAddress): Array<string> {

    if (((address.streetNum && address.streetName) || address.ruralRouteTypeCd) &&
        address.cityName &&
        address.territory &&
        address.postalCode &&
        address.country
    ) {
      return this.userProfileUtilsService.formatAddress(address);
    }

    return this.formateAddressLines(address);
  }

  private getAddressLines(profile: W8BenProfile, addressUsage: W8BenAddressUsageCd = 'HOME_ADDRESS'): Array<string> | undefined {
    const contactAddress = profile.contactStreetAddresses.find(
      (contactAddress: W8BenContactAddress) =>
        contactAddress.usageCd === addressUsage
    );

    return contactAddress ? this.formatAddress(contactAddress.address) : undefined;
  }

  private getTaxResidencyFromProfile(profile: W8BenProfile): string {
    const contactAddress = profile.contactStreetAddresses.find(
      (contactAddress: W8BenContactAddress) =>
        contactAddress.usageCd === 'HOME_ADDRESS'
    );

    return contactAddress?.address?.country ?? '';
  }

  private updatePreference(): void {
    const w8benPreference = {
      W8BEN_INDICATOR: false
    };
    this.preferencesService.updateUserPreferencesCache(w8benPreference);
  }

  private formateAddressLines(address: W8BenAddress): Array<string> {
    const addressLines = address.addressLine?.filter((line: string) => (line ?? null) !== null) ?? [];

    const addressLine: Array<string> = [];
    addressLine.push(address.cityName ?? ' ');
    addressLine.push(address.territory ?? ' ');
    addressLine.push(PostalCodeService.formatCanadianPostalCode(address.postalCode) ?? ' ');

    addressLines.push(addressLine.filter((line: string) => line !== ' ').join(', ')?.toUpperCase());
    addressLines.push(address.country?.toUpperCase() ?? ' ');

    return addressLines
  }
}

